import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output } from '@angular/core';
import { CommonButtonRowWidget, HOURNAMES_MAP, HOURS_ET, MATCHING_FULL_DAY, UrlService, WeekdayIndexEnum } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { BlockoutSlots, DAY_INDICES, SelectedSlots, SLOTS_DAY_MAP, StandardFourTimeZoneId, WeekdayIndex } from '@interfaces';
import { ScheduleService } from '../schedule.service';

@Component({
	selector: 'schedule-table-part',
	standalone: true,
	imports: [CommonButtonRowWidget],
	templateUrl: './schedule-table.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./schedule-table.part.scss'],
})

export class ScheduleTablePart implements OnInit {

	public meetingsId = input.required<number>();
	public selectedSlots = input.required<SelectedSlots | undefined>();
	public blockoutSlots = input<BlockoutSlots[]>([]); // array of acc classes
	public timeZoneId = input.required<StandardFourTimeZoneId>();
	public minimumSelectionSlots = input.required<number>();

	public saved = output<void>(); // Emits after saving

	public service = inject(ScheduleService);
	public dialogService = inject(DialogService);
	public commonLabels = inject(UrlService).commonLabels;

	dayIndices = DAY_INDICES;
	public count = 0;
	public selectedSlotsArr: boolean[][] = Array(this.dayIndices.length).fill(null).map(() => Array(HOURS_ET.length).fill(false))
	public blockoutArr: boolean[][] = Array(this.dayIndices.length).fill(null).map(() => Array(HOURS_ET.length).fill(false));
	public impliedSelectedArr: boolean[][] = Array(this.dayIndices.length).fill(null).map(() => Array(HOURS_ET.length).fill(false));

	public hourNames = computed<string[]>(() => {
		const timeZoneId = this.timeZoneId();
		const hourNames = HOURNAMES_MAP[timeZoneId]!;
		return hourNames;
	});

	ngOnInit() {
		this.initData();
	}

	initData() {
		this.setSelectedSlotsArr();

		// Mark blockout slots and update selectedSlotsArr with applicant's selection
		this.blockoutSlots().forEach(s => {
			const { dayIndex, startTimeIndex, applicantSelectedAccSlot } = s;

			if (this.blockoutArr[dayIndex]) {
				this.blockoutArr[dayIndex][startTimeIndex] = true;
			}
			if (this.selectedSlotsArr[dayIndex]) {
				this.selectedSlotsArr[dayIndex][startTimeIndex] = applicantSelectedAccSlot
			}
		})

		this.calculateImpliedSelections();
		this.calculateSelectedCount();
		// set the initial selected slots and count
		this.setUpdatedSelectedSlots();
	}

	setSelectedSlotsArr() {
		const selectedSlots = this.selectedSlots();
		if (!selectedSlots) return;

		this.dayIndices.forEach(dayIndex => {
			const dayArray = this.selectedSlotsArr[dayIndex];
			if (!dayArray) return;

			const dayKey = SLOTS_DAY_MAP[dayIndex];
			const scheduleString = selectedSlots[dayKey];
			for (let i = 0; i < scheduleString.length; i++) {
				dayArray[i] = scheduleString[i] !== '-';
			}
		});

		this.calculateImpliedSelections();
	}


	calculateSelectedCount() {
		this.count = this.selectedSlotsArr.flat().reduce((total, selection) => total + (selection ? 1 : 0), 0);
	};

	/**
	 * Calculates implied selections based on overlapping time slots
	 * If previous and next time slots are selected, the current slot is considered implied
	 */
	calculateImpliedSelections() {
		// Reset implied selections
		this.impliedSelectedArr = Array(this.dayIndices.length).fill(null).map(() => Array(HOURS_ET.length).fill(false));

		for (let dayIndex = 0; dayIndex < this.dayIndices.length; dayIndex++) {
			for (let hourIndex = 1; hourIndex < HOURS_ET.length - 1; hourIndex++) {
				const currentSlot = this.selectedSlotsArr[dayIndex]?.[hourIndex];
				const prevSlot = this.selectedSlotsArr[dayIndex]?.[hourIndex - 1];
				const nextSlot = this.selectedSlotsArr[dayIndex]?.[hourIndex + 1];

				// If current slot is not selected but previous and next slots are selected, mark it as implied
				if (!currentSlot && prevSlot && nextSlot) {
					this.impliedSelectedArr[dayIndex]![hourIndex] = true;
				}
			}
		}
	};

	// toggles selection state of a time slot
	toggleSelection(dayIndex: WeekdayIndex, hourIndex: number) {
		if (!this.selectedSlotsArr[dayIndex]) return;

		this.selectedSlotsArr[dayIndex][hourIndex] = !this.selectedSlotsArr[dayIndex][hourIndex];
		this.count += this.selectedSlotsArr[dayIndex][hourIndex] ? 1 : -1
		this.calculateImpliedSelections();
		this.setUpdatedSelectedSlotsForDay(dayIndex);
	};


	/**
	 * Toggles selection state for an entire day
	 * If any slot in the day is selected, deselects all slots .If no slots are selected, selects all available slots
	 * @param index Day index (0-6 for Mon-Sun)
	*/
	toggleDaySelection(index: WeekdayIndex) {

		if (!this.service.isEditMode()) return;

		const dayArr = this.selectedSlotsArr[index];
		if (!dayArr) return;

		const hasAnySelected = dayArr.some(h => !!h);
		const selectionStatus = !hasAnySelected;

		for (let i = 0; i < dayArr.length; i++) {
			dayArr[i] = selectionStatus;
		}
		this.calculateSelectedCount();
		this.calculateImpliedSelections();
		this.setUpdatedSelectedSlotsForDay(index);
	};

	/**
	 * Toggles selection state for a specific hour across all days
	 * If any slot in that hour is selected, deselects all slots. If no slots are selected, selects all available slots
	 * @param index Hour index (0-16 representing 6am-10pm ET)
	*/
	toggleHourSelection(index: number) {

		if (!this.service.isEditMode()) return;

		const hasAnySelected = this.selectedSlotsArr.some(d => (
			d.some((h, hourIndex) => h && hourIndex == index))
		);
		const selectionStatus = !hasAnySelected;

		this.selectedSlotsArr.forEach((row, i) => { row[index] = selectionStatus });

		this.calculateSelectedCount();
		this.calculateImpliedSelections();
		this.setUpdatedSelectedSlots();
	};


	/**
	 * Converts boolean array of selections for a day into string format
	 * @param day_idx Day index (0-6, Monday-Sunday)
	 * @returns String where each character represents a time slot:
	 *          '-' for unselected, characters from matchingFullDay for selected
	*/
	selectedMeetingsByDay(day_idx: WeekdayIndex) {
		const selection = [];
		for (let j = 0; j < HOURS_ET.length; j++) {
			if (this.selectedSlotsArr[day_idx]![j]) {
				selection.push(MATCHING_FULL_DAY[j]);
			} else {
				selection.push('-');
			}
		}
		return selection.join('');
	};

	setUpdatedSelectedSlots() {
		this.service.setUpdatedSelectedSlots({
			numMeetings: this.count,
			mon: this.selectedMeetingsByDay(WeekdayIndexEnum.Mon),
			tue: this.selectedMeetingsByDay(WeekdayIndexEnum.Tue),
			wed: this.selectedMeetingsByDay(WeekdayIndexEnum.Wed),
			thu: this.selectedMeetingsByDay(WeekdayIndexEnum.Thu),
			fri: this.selectedMeetingsByDay(WeekdayIndexEnum.Fri),
			sat: this.selectedMeetingsByDay(WeekdayIndexEnum.Sat),
			sun: this.selectedMeetingsByDay(WeekdayIndexEnum.Sun)
		});
	};

	setUpdatedSelectedSlotsForDay(dayIndex: WeekdayIndex) {
		this.service.setUpdatedSelectedSlotsForDay(this.count, dayIndex, this.selectedMeetingsByDay(dayIndex));
	}

	async saveChanges() {
		const updatedSelectedSlots = this.service.updatedSelectedSlots();
		if (!updatedSelectedSlots) return;

		const minimumRequired = this.minimumSelectionSlots();
		if (updatedSelectedSlots?.numMeetings < minimumRequired) {
			this.dialogService.showMessage('Insufficient Selections', `You must select at least ${minimumRequired} meetings.`, 'Okay', [], 300, 200);
			return;
		}

		// If there are blockout slots, ensure at least one is selected, if not show a message
		if (this.blockoutSlots().length > 0) {
			const blockoutSelected = this.blockoutSlots().some(block => {
				const dayIndex = block.dayIndex;
				const startTimeIndex = block.startTimeIndex;
				return this.selectedSlotsArr[dayIndex] && this.selectedSlotsArr[dayIndex][startTimeIndex];
			});
			if (!blockoutSelected) {
				this.dialogService.showMessage('Blockout Slots', `You must select at least one blockout slot.`, 'Okay', [], 300, 200);
				return;
			}
		}

		await this.service.updateMeetings(this.meetingsId(), updatedSelectedSlots);
		this.saved.emit();
	}

	onCancel() {
		this.initData();
		this.service.setIsEditMode(false);
	}
}